import * as customFlexGrid from '@/components/molecules/CustomFlexGrid';
import * as customMultiRow from '@/components/molecules/CustomMultiRow';

/**
 * グリッド項目一覧.
 *
 * CustomFlexGrid に渡す項目一覧を作成するヘルパークラス。
 *
 * | メソッド        | 説明
 * | --------------- | -------------------
 * | {@link text()}  | 文字列項目作成
 * | {@link numb()}  | 数値項目作成
 * | {@link date()}  | 日付項目作成
 * | {@link link()}  | リンク項目作成
 * | {@link list()}  | リスト項目作成
 * | {@link cell()}  | ボタン項目作成
 * | {@link item()}  | 項目作成(汎用)
 * | {@link add()}   | 項目追加(汎用)
 * | {@link group()} | 項目グループ作成
 */
export class GridItems {

  /**
   * コンストラクタ
   */
  constructor() {

    // 項目一覧
    this.items = [];
  }

  /**
   * 項目追加 - 文字列型
   *
   * @param {string} binding  項目名
   * @param {number} width    項目幅
   * @param {string} header   項目ラベル
   * @param {object} options  その他設定
   */
  text(binding, width, header, options) {
    return this.item(binding, width, header, 'String', options);
  }

  /**
   * 項目追加 - 数値型
   *
   * @param {string} binding  項目名
   * @param {number} width    項目幅
   * @param {string} header   項目ラベル
   * @param {object} options  その他設定
   */
  numb(binding, width, header, options) {
    return this.item(binding, width, header, 'Number', options);
  }

  /**
   * 項目追加 - 日付型
   *
   * @param {string} binding  項目名
   * @param {number} width    項目幅
   * @param {string} header   項目ラベル
   * @param {object} options  その他設定
   */
  date(binding, width, header, options) {
    return this.item(binding, width, header, 'Date', options);
  }

  /**
   * 項目追加 - リンク型
   *
   * @param {string} binding  項目名
   * @param {number} width    項目幅
   * @param {string} header   項目ラベル
   * @param {object} options  その他設定
   */
  link(binding, width, header, options) {
    return this.item(binding, width, header, 'link', options);
  }

  /**
   * 項目追加 - リスト型
   *
   * @param {string} binding  項目名
   * @param {number} width    項目幅
   * @param {string} header   項目ラベル
   * @param {object} options  その他設定
   */
   list(binding, width, header, options) {
    return this.item(binding, width, header, 'list', options);
  }

  /**
   * 項目追加 - ボタン型
   *
   * @param {string} binding  項目名
   * @param {number} width    項目幅
   * @param {string} header   項目ラベル
   * @param {object} options  その他設定
   */  cell(binding, width, header, options) {
    return this.item(binding, width, header, 'cellMaker', options);
  }

  /**
   * 項目追加 - 型なし
   *
   * @param {string} binding  項目名
   * @param {number} width    項目幅
   * @param {string} header   項目ラベル
   * @param {object} options  その他設定
   */
  none(binding, width, header, options) {
    this.items.push({
      binding,
      header, 
      width,
      ...options,
    });
    return this;
  }

  /**
   * 項目作成
   *
   * @param {string} binding  項目名
   * @param {number} width    項目幅
   * @param {string} header   項目ラベル
   * @param {string} dataType 項目データ型
   * @param {object} options  その他設定
   */
  item(binding, width, header, dataType, options) {
    this.items.push({
      binding,
      header,
      dataType,
      width,
      key: 'column-' + binding,
      ...options,
    });
    return this;
  }

  /**
   * 項目追加
   */
  add(item) {
    this.items.push(item);
    return this;
  }

  /**
   * 項目グループ追加
   *
   * @param {string} header 項目ラベル
   */
  group(header) {
    return new GridItemGroup(header, this);
  }

  /**
   * グリッド項目コンポーネント作成
   *
   * @param options 設定オプション
   * @param readOnly 読み込み専用
   * @return グリッド項目コンポーネント配列
   */
  createComponents(options = {}, readOnly = false) {
    const items = this.createCustomItems(options, readOnly);
    return customFlexGrid.createComponents(items, readOnly);
  }

  /**
   * 複数行グリッド項目コンポーネント作成
   *
   * @param options 設定オプション
   * @param readOnly 読み込み専用
   * @return グリッド項目コンポーネント配列
   */
  createMultiRow(options = {}, readOnly = false) {
    const items = this.createCustomItems(options, readOnly);
    return customMultiRow.createComponents(items, readOnly);
  }

  // グリッド項目編集
  createCustomItems(options = {}, readOnly = false) {
    return this.items.map((item) => {

      // リンク項目のクリック処理を設定
      if (item.dataType === 'link' && !item.click) {
        const name = item.binding;
        const handler = options[name]?.click;
        const newItem = {
          ...item,
          click: (ev, ctx) => this.handleLinkClick(ctx, handler),
        };
        return newItem;
      }

      // データマップ属性を設定
      if (item.dataType === 'list' && !item.dataMap) {
        const name = item.binding;
        const dataMap = options[name]?.dataMap;
        const newItem = {
          ...item,
          dataType: 'String',
          dataMap: dataMap,
        };
        return newItem;
      }
      return item;
    }).reduce((result, item) => {
      result[item.binding] = item;
      return result;
    }, {});
  }

  /**
   * リンク押下処理
   *
   * @param {object} context 実行コンテキスト
   * @param {function} handler ハンドラー
   */
  handleLinkClick(context, handler) {
    const name = context.col.binding;
    const item = context.item;

    // ハンドラー未定義 - 警告ログ出力
    if (!handler) {
      console.warn('Click link ignored',
          `${name}=${context.value}`, item);
      return;
    }
    console.info('Click link', `${name}=${context.value}`);

    // ハンドラー実行
    handler(item);
  }
}

/**
 * グリッド項目グループ
 */
export class GridItemGroup extends GridItems {
  /**
   * コンストラクタ
   *
   * @param {string} header 項目ラベル
   * @param {GridItems} parent 親項目
   */
  constructor(header, parent) {
    super();
    this.header = header;
    this.parent = parent;
  }

  /**
   * 項目グループ終了
   */
  end() {
    for (const item of this.items) {
      item.groupHeader = this.header;
      this.parent.items.push(item);
    }
    return this.parent;
  }
}

// デフォルトエクスポート
export default GridItems;
